import React, { ErrorInfo, ReactNode, Suspense } from 'react'
import { PageError } from '../PageError/PageError'
import { ViewerAppError } from '../../errors/viewer-app-error'
import { NoBillingPackageError } from '../../../../../trillion-common/src/API/errors/NoBillingPackageError'
import { IncorrectActivationKeyError } from '../../../../../trillion-common/src/API/errors/IncorrectActivationKeyError'
import { captureError } from '../../../../../trillion-common/src/monitoring/captureError'
import { ProductNotExistOrNotPublishedError } from '../../../../../trillion-common/src/API/errors/NoProductExistOrNotPublished'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error | ViewerAppError
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureError(error)
    console.error(error, errorInfo)
  }

  reloadApp = () => {
    this.setState({ hasError: false })
  }

  render() {
    const { hasError, error } = this.state
    let message = ''
    let canReload = true
    if (hasError) {
      if (error instanceof ViewerAppError) {
        message = error.message

        if (
          error.cause instanceof NoBillingPackageError ||
          error.cause instanceof IncorrectActivationKeyError ||
          error.cause instanceof ProductNotExistOrNotPublishedError
        ) {
          canReload = false
        }
      } else {
        message = 'Something went wrong. Please reload the App and try again.'
      }
    }
    const { children } = this.props

    if (hasError) {
      return (
        <Suspense fallback={null}>
          <PageError userMessage={message} reloadApp={this.reloadApp} canReload={canReload} />
        </Suspense>
      )
    }

    return children
  }
}
